import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  ComposedChart
} from 'recharts';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  ShoppingCart, 
  Users,
  Store,
  Activity,
  RefreshCw,
  Loader2,
  ArrowUp,
  ArrowDown,
  Clock,
  Download,
  FileText,
  Calendar,
  Database,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color palette for charts
const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const FinancialDashboard = () => {
  const { getAuthHeader } = useAuth();
  const { isConnected, subscribe } = useWebSocket();
  const candlestickRef = useRef(null);
  const chartRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [candlestickData, setCandlestickData] = useState([]);
  const [outletPerformance, setOutletPerformance] = useState([]);
  const [subscriptionBreakdown, setSubscriptionBreakdown] = useState([]);
  const [hourlyDistribution, setHourlyDistribution] = useState([]);
  const [weeklyComparison, setWeeklyComparison] = useState(null);
  const [realtimeOrders, setRealtimeOrders] = useState([]);
  
  // Date filter state
  const [dateRange, setDateRange] = useState('30'); // days
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('all');

  const fetchAllData = useCallback(async (days = 30) => {
    try {
      const headers = getAuthHeader();
      const [
        overviewRes,
        revenueTrendRes,
        candlestickRes,
        outletPerfRes,
        subscriptionRes,
        hourlyRes,
        weeklyRes,
        realtimeRes,
        outletsRes
      ] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers }),
        axios.get(`${API}/analytics/revenue-trend?days=${days}`, { headers }),
        axios.get(`${API}/analytics/candlestick?days=${days}`, { headers }),
        axios.get(`${API}/analytics/outlet-performance`, { headers }),
        axios.get(`${API}/analytics/subscription-breakdown`, { headers }),
        axios.get(`${API}/analytics/hourly-distribution`, { headers }),
        axios.get(`${API}/analytics/weekly-comparison`, { headers }),
        axios.get(`${API}/analytics/realtime-orders?limit=10`, { headers }),
        axios.get(`${API}/outlets`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setRevenueTrend(revenueTrendRes.data);
      setCandlestickData(candlestickRes.data);
      setOutletPerformance(outletPerfRes.data);
      setSubscriptionBreakdown(subscriptionRes.data);
      setHourlyDistribution(hourlyRes.data);
      setWeeklyComparison(weeklyRes.data);
      setRealtimeOrders(realtimeRes.data);
      setOutlets(outletsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch analytics:', e);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchAllData(parseInt(dateRange));
  }, [fetchAllData, dateRange]);

  // Handle date range change
  const handleDateRangeChange = (value) => {
    setDateRange(value);
    const days = parseInt(value);
    setStartDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setEndDate(format(new Date(), 'yyyy-MM-dd'));
    setRefreshing(true);
    fetchAllData(days);
  };

  // Seed sample data
  const handleSeedData = async () => {
    if (!window.confirm('This will generate sample orders for the past 60 days. Continue?')) return;
    
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/analytics/seed-sample-data`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Created ${response.data.orders_created} sample orders!`);
      fetchAllData(parseInt(dateRange));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedOutlet !== 'all') params.append('outlet_id', selectedOutlet);
      
      const response = await axios.get(`${API}/reports/export/csv?${params.toString()}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `deliman_report_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (e) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  // Export PDF (opens in new tab for printing)
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedOutlet !== 'all') params.append('outlet_id', selectedOutlet);
      
      const response = await axios.get(`${API}/reports/export/pdf?${params.toString()}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF report opened');
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Setup candlestick chart
  useEffect(() => {
    if (!candlestickRef.current || candlestickData.length === 0) return;

    // Clear previous chart
    if (chartRef.current) {
      chartRef.current.remove();
    }

    const chart = createChart(candlestickRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
      },
      width: candlestickRef.current.clientWidth,
      height: 300,
      grid: {
        vertLines: { color: 'rgba(156, 163, 175, 0.1)' },
        horzLines: { color: 'rgba(156, 163, 175, 0.1)' },
      },
      timeScale: {
        borderColor: 'rgba(156, 163, 175, 0.2)',
      },
      rightPriceScale: {
        borderColor: 'rgba(156, 163, 175, 0.2)',
      },
    });

    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#10b981',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
    });

    candlestickSeries.setData(candlestickData);
    chart.timeScale().fitContent();
    chartRef.current = chart;

    // Handle resize
    const handleResize = () => {
      if (candlestickRef.current) {
        chart.applyOptions({ width: candlestickRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [candlestickData]);

  // Subscribe to real-time order updates
  useEffect(() => {
    if (!subscribe) return;
    
    const unsubscribe = subscribe('order_update', (data) => {
      if (data.action === 'created') {
        // Add to real-time feed
        setRealtimeOrders(prev => [{
          id: data.order_id,
          outlet_name: 'New Order',
          status: data.status,
          location: data.order?.location,
          item_details: data.order?.item_details,
          created_at: data.order?.created_at
        }, ...prev.slice(0, 9)]);
        
        // Update overview stats
        setOverview(prev => prev ? {
          ...prev,
          total_orders: prev.total_orders + 1,
          pending_orders: prev.pending_orders + 1
        } : prev);
      }
    });

    return () => unsubscribe && unsubscribe();
  }, [subscribe]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllData();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'text-blue-500';
      case 'delivered_paid': return 'text-emerald-500';
      case 'delivered_no_payment': return 'text-amber-500';
      case 'rejected': return 'text-red-500';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Financial Analytics</h2>
          <p className="text-muted-foreground">
            Real-time insights and performance metrics
            {isConnected && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-600">
                <Activity className="h-3 w-3 animate-pulse" />
                Live
              </span>
            )}
          </p>
        </div>
        
        {/* Filters and Actions */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range Filter */}
          <Select value={dateRange} onValueChange={handleDateRangeChange}>
            <SelectTrigger className="w-36" data-testid="date-range-filter">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Outlet Filter */}
          <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
            <SelectTrigger className="w-40" data-testid="outlet-filter">
              <Store className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All outlets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outlets</SelectItem>
              {outlets.map((outlet) => (
                <SelectItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Export Buttons */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleExportCSV}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleExportPDF}
              data-testid="export-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-1" />
              PDF
            </Button>
          </div>
          
          {/* Seed Sample Data Button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleSeedData}
            disabled={seeding}
            data-testid="seed-data-btn"
          >
            {seeding ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1" />
            )}
            Sample Data
          </Button>
          
          {/* Refresh */}
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <DollarSign className="h-5 w-5 text-emerald-500" />
            <span className="text-xs text-muted-foreground">Revenue</span>
          </div>
          <p className="text-2xl font-bold mt-2">{formatCurrency(overview?.total_revenue || 0)}</p>
          <p className="text-xs text-muted-foreground">Total earnings</p>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <Activity className="h-5 w-5 text-blue-500" />
            <span className="text-xs text-muted-foreground">MRR</span>
          </div>
          <p className="text-2xl font-bold mt-2">{formatCurrency(overview?.mrr || 0)}</p>
          <p className="text-xs text-muted-foreground">Monthly recurring</p>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <ShoppingCart className="h-5 w-5 text-violet-500" />
            <span className="text-xs text-muted-foreground">Orders</span>
          </div>
          <p className="text-2xl font-bold mt-2">{overview?.total_orders || 0}</p>
          <p className="text-xs text-muted-foreground">{overview?.completion_rate}% completed</p>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <Users className="h-5 w-5 text-amber-500" />
            <span className="text-xs text-muted-foreground">Subscribers</span>
          </div>
          <p className="text-2xl font-bold mt-2">{overview?.active_subscribers || 0}</p>
          <p className="text-xs text-muted-foreground">Active plans</p>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <Store className="h-5 w-5 text-pink-500" />
            <span className="text-xs text-muted-foreground">Outlets</span>
          </div>
          <p className="text-2xl font-bold mt-2">{overview?.total_outlets || 0}</p>
          <p className="text-xs text-muted-foreground">Registered</p>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center justify-between">
            {weeklyComparison?.trend === 'up' ? (
              <TrendingUp className="h-5 w-5 text-emerald-500" />
            ) : weeklyComparison?.trend === 'down' ? (
              <TrendingDown className="h-5 w-5 text-red-500" />
            ) : (
              <Activity className="h-5 w-5 text-muted-foreground" />
            )}
            <span className="text-xs text-muted-foreground">WoW</span>
          </div>
          <p className={`text-2xl font-bold mt-2 ${
            weeklyComparison?.growth_percent > 0 ? 'text-emerald-600' : 
            weeklyComparison?.growth_percent < 0 ? 'text-red-600' : ''
          }`}>
            {weeklyComparison?.growth_percent > 0 ? '+' : ''}{weeklyComparison?.growth_percent || 0}%
          </p>
          <p className="text-xs text-muted-foreground">vs last week</p>
        </Card>
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="revenue">Revenue Trend</TabsTrigger>
          <TabsTrigger value="candlestick">Order Activity</TabsTrigger>
          <TabsTrigger value="outlets">Outlet Performance</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="hourly">Hourly Pattern</TabsTrigger>
        </TabsList>

        {/* Revenue Trend - Area Chart */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader>
              <CardTitle>30-Day Revenue & Orders Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(156, 163, 175, 0.2)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.slice(5)}
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#10b981"
                      fill="url(#revenueGradient)"
                      name="Revenue ($)"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="orders"
                      fill="#2563eb"
                      opacity={0.8}
                      name="Orders"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="completed"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={false}
                      name="Completed"
                    />
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Candlestick Chart - Order Activity */}
        <TabsContent value="candlestick">
          <Card>
            <CardHeader>
              <CardTitle>Order Activity (Candlestick View)</CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={candlestickRef} className="h-80" data-testid="candlestick-chart" />
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Green candles indicate higher evening activity, red indicates lower
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outlet Performance - Bar Chart */}
        <TabsContent value="outlets">
          <Card>
            <CardHeader>
              <CardTitle>Top Outlets by Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={outletPerformance.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(156, 163, 175, 0.2)" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis 
                      dataKey="outlet_name" 
                      type="category" 
                      width={120}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
                    <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                    <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rejected" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Breakdown - Pie Chart */}
        <TabsContent value="subscriptions">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Subscription Tiers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={subscriptionBreakdown.filter(s => s.count > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {subscriptionBreakdown.map((entry, index) => (
                          <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue by Tier</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {subscriptionBreakdown.map((tier, index) => (
                    <div key={tier.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="font-medium">{tier.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(tier.revenue)}</p>
                        <p className="text-xs text-muted-foreground">{tier.count} subscribers</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Hourly Distribution - Area Chart */}
        <TabsContent value="hourly">
          <Card>
            <CardHeader>
              <CardTitle>Order Distribution by Hour</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={hourlyDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(156, 163, 175, 0.2)" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="orders"
                      stroke="#8b5cf6"
                      fill="url(#hourlyGradient)"
                      name="Orders"
                    />
                    <defs>
                      <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Real-time Orders Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Live Orders Feed
            {isConnected && (
              <span className="ml-auto text-xs font-normal text-emerald-600 flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                Connected
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {realtimeOrders.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No recent orders</p>
            ) : (
              realtimeOrders.map((order) => (
                <div 
                  key={order.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg animate-fade-in"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      order.status === 'new' ? 'bg-blue-500' :
                      order.status === 'delivered_paid' ? 'bg-emerald-500' :
                      order.status === 'delivered_no_payment' ? 'bg-amber-500' :
                      'bg-red-500'
                    }`} />
                    <div>
                      <p className="font-mono text-sm font-semibold">
                        #{order.id?.slice(0, 8).toUpperCase()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {order.outlet_name} • {order.item_details || 'No items'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={getStatusColor(order.status)}>
                      {order.status?.replace('_', ' ')}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 justify-end">
                      <Clock className="h-3 w-3" />
                      {order.created_at ? new Date(order.created_at).toLocaleTimeString() : 'Now'}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialDashboard;
